import React, { useEffect, useState, useContext, useRef } from 'react';
import {
    Container,
    Box,
    Tabs,
    Tab,
    Typography,
    Button,
    Paper,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import Header from '../components/Header';
import Footer from '../components/Footer';
import SnackbarAlert from '../components/SnackbarAlert';
import TabContent from '../components/TabContent';
import CommentContext from '../context/CommentContext';
import '../App.css';

import ExportPdf from '../utils/ExportPdf';


function parseSummaryWithBold(text) {
    if (!text) return '';
    const lines = text.split(/\r?\n/);
    return lines.map((line, idx) => {
        // Handle #### (subheader)
        if (line.startsWith('#### ')) {
            return (
                <Typography key={idx} variant="subtitle1" sx={{ color: '#90caf9', fontWeight: 700, mt: 2, mb: 1, fontFamily: 'Space Mono' }}>
                    {line.replace(/^#### /, '')}
                </Typography>
            );
        }
        // Handle ### (header)
        if (line.startsWith('### ')) {
            return (
                <Typography key={idx} variant="h6" sx={{ color: '#1976d2', fontWeight: 700, mt: 3, mb: 1, fontFamily: 'Space Mono' }}>
                    {line.replace(/^### /, '')}
                </Typography>
            );
        }
        // Handle bold (**text**)
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return (
            <span key={idx}>
                {parts.map((part, i) =>
                    part.startsWith('**') && part.endsWith('**')
                        ? <b key={i}>{part.slice(2, -2)}</b>
                        : part
                )}
                <br />
            </span>
        );
    });
}

function Evaluator() {
    /* ---------- get comments from context ---------- */
    const { comments: ctxComments } = useContext(CommentContext);

    /* ---------- tabs & error handling ---------- */
    const [tabIndex, setTabIndex] = useState(0);
    const [error, setError] = useState('');
    const [showError, setShowError] = useState(false);
    const exportRef = useRef(null);

    const handleError = (msg) => {
        setError(msg);
        setShowError(true);
    };
    const handleTabChange = (_, v) => setTabIndex(v);
    const handleCloseError = () => setShowError(false);

    // Prepare data for each tab
    const selfAssessment = ctxComments?.self_assessment || {};
    const documents = ctxComments?.documents || {};
    const overall = ctxComments?.overall || {};
    const summary = ctxComments?.summary || '';

    const theme = useTheme();

    return (
        <Container maxWidth="md" sx={{ py: 6 }}>
        <Header title="Evaluation Results" />
        <SnackbarAlert
            error={error}
            showError={showError}
            handleCloseError={handleCloseError}
        />

        <Tabs
            value={tabIndex}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            sx={{ mb: 2 }}
        >
            <Tab
            label="Self-Assessment"
            sx={{ backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily }}
            />
            <Tab
            label="Documents"
            sx={{ backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily }}
            />
            <Tab
            label="Overall"
            sx={{ backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily }}
            />
            <Tab
            label="Summary"
            sx={{ backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily }}
            />
            <Tab
            label="Export"
            sx={{ backgroundColor: theme.palette.background.paper, color: theme.palette.text.primary, fontFamily: theme.typography.fontFamily }}
            />
        </Tabs>

        {/* Export tab */}
        {tabIndex === 4 ? (
            <Box>
            <Typography
                variant="h6"
                sx={{ color: theme.palette.text.white, fontFamily: theme.typography.fontFamily, mb: 2 }}
            >
                Export as PDF
            </Typography>
            <Button
                variant="contained"
                color="success"
                onClick={() => ExportPdf(selfAssessment, documents, overall, summary)}
                >
                Export PDF
            </Button>
            </Box>
        ) : tabIndex === 3 ? (
            <Paper
            elevation={2}
            sx={{
                p: 2,
                mb: 2,
                backgroundColor: theme.palette.background.paper,
            }}
            >
            <Typography
                sx={{
                color: theme.palette.text.primary,
                fontFamily: theme.typography.fontFamily,
                whiteSpace: "pre-line",
                }}
            >
                {summary ? parseSummaryWithBold(summary) : "No summary available."}
            </Typography>
            </Paper>
        ) : (
            <TabContent
            tabIndex={tabIndex}
            selfAssessment={selfAssessment}
            documents={documents}
            overall={overall}
            />
        )}
        <Footer />
        </Container>
    );
};

export default Evaluator;
